import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { OverlayPanel } from 'primeng/overlaypanel';
import { BehaviorSubject, combineLatest, finalize, from, map, Observable, shareReplay, switchMap, take, timer } from 'rxjs';
import { IFilterModel } from 'src/app/shared/models/filter-model';
import { SelectedFilterChip } from 'src/app/shared/interfaces/selected-filter-chip.interface';
import { InvoiceStatus } from '../../../domain/enums/invoice-status.enum';
import { InvoiceFacade } from '../../facades/invoice.facade';
import { InvoiceCardActionEvent } from '../../components/invoice-card-list/invoice-card-list.component';
import { FilterService } from 'src/app/shared/services/filter.service';
import { InvoiceExportPeriod, InvoiceExportRequest } from '../../../domain/models/invoice-export.model';
import { DownloadUtil } from 'src/app/shared/utils/download-util';
import { IFilter } from 'src/app/shared/interfaces/ifilter';
import { InvoiceAction } from '../../../domain/enums/invoice-action.enum';
import { Invoice } from '../../../domain/entities/invoice.entity';

interface StatusOption {
  id: InvoiceStatus;
  name: string;
  checked: boolean;
}

interface ExportContext {
  period: InvoiceExportPeriod;
  filters: readonly IFilter[];
  hasActiveFilters: boolean;
}

interface ExportButtonVm {
  label: string;
  severityClass: string;
  exporting: boolean;
}

interface ExportPopoverVm {
  periodLabel: string;
  monthTotal: number | null;
  successMessage: string | null;
  errorMessage: string | null;
}

@Component({
  selector: 'app-invoice-list-container',
  templateUrl: './invoice-list-container.component.html',
  styleUrls: ['./invoice-list-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class InvoiceListContainerComponent implements OnInit {
  @ViewChild('csvExportPanel') csvExportPanel!: OverlayPanel;

  readonly vm$ = this.facade.vm$;
  readonly currentMonthDate = new Date();
  readonly activeFilterChips$: Observable<SelectedFilterChip[]> = this.facade.filters$.pipe(
    map((filters) => this.buildChips(filters)),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  selectedEditInvoice: Invoice | null = null;

  private readonly exportContext$: Observable<ExportContext> = this.facade.filters$.pipe(
    map((filters) => this.buildExportContext(filters)),
    shareReplay({ bufferSize: 1, refCount: true })
  );
  private readonly exportSuccessSubject = new BehaviorSubject<string | null>(null);
  private readonly exportErrorSubject = new BehaviorSubject<string | null>(null);
  private readonly exportingSubject = new BehaviorSubject<boolean>(false);
  private readonly defaultExportStatuses: readonly string[] = [
    InvoiceStatus.Issued,
    InvoiceStatus.RegisterError,
    InvoiceStatus.Created,
    InvoiceStatus.Failed,
    InvoiceStatus.Canceled,
    InvoiceStatus.SentEmail,
    InvoiceStatus.Pending
  ];

  readonly statusOptions: StatusOption[] = [
    { id: InvoiceStatus.Issued, name: 'Emitida', checked: false },
    { id: InvoiceStatus.Created, name: 'Criada', checked: false },
    { id: InvoiceStatus.RegisterError, name: 'Erro de Cadastro', checked: false },
    { id: InvoiceStatus.Failed, name: 'Falha', checked: false },
    { id: InvoiceStatus.Canceled, name: 'Cancelada', checked: false },
    { id: InvoiceStatus.SentEmail, name: 'E-mail Enviado', checked: false },
    { id: InvoiceStatus.Pending, name: 'Pendente', checked: false }
  ];

  readonly exporting$ = this.exportingSubject.asObservable();
  readonly exportButtonVm$: Observable<ExportButtonVm> = combineLatest([
    this.exportContext$,
    this.vm$,
    this.exportingSubject
  ]).pipe(
    map(([context, vm, exporting]) => ({
      label: this.buildExportButtonLabel(context, vm.total),
      severityClass: context.hasActiveFilters ? 'invoice-export-button--filtered' : '',
      exporting
    })),
    shareReplay({ bufferSize: 1, refCount: true })
  );
  readonly exportPopoverVm$: Observable<ExportPopoverVm> = this.exportContext$.pipe(
    switchMap((context) =>
      combineLatest([
        this.exportSuccessSubject,
        this.exportErrorSubject
      ]).pipe(
        map(([successMessage, errorMessage]) => ({
          periodLabel: `${this.getMonthName(context.period.month)} ${context.period.year} - mês completo`,
          monthTotal: null,
          successMessage,
          errorMessage
        }))
      )
    ),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  private readonly filterLabelMap: Record<string, string> = {
    date: 'Data',
    status: 'Status',
    professional: 'Prestador',
  };

  constructor(
    private readonly facade: InvoiceFacade,
    private readonly filterService: FilterService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.facade.currentFilters.forEach((filter) => {
      this.filterService.filters = filter;
    });
    this.facade.load();
  }

  onSearch(text: string): void {
    this.facade.search(text);
  }

  onFilter(filterModels: IFilterModel[]): void {
    this.facade.filterBy(filterModels);
  }

  onRemoveFilter(chip: SelectedFilterChip): void {
    this.filterService.remove(chip.value as IFilterModel);
  }

  onClearAllFilters(): void {
    this.filterService.clear('invoiceCardFilter');
  }

  onLoadMore(): void {
    this.facade.loadMore();
  }

  onAction(event: InvoiceCardActionEvent): void {
    if (event.action === InvoiceAction.Edit) {
      this.selectedEditInvoice = event.invoice;
      this.cdr.markForCheck();
      return;
    }

    this.facade.runAction(event.invoice, event.action);
  }

  onEditModalSuccess(): void {
    this.selectedEditInvoice = null;
    this.cdr.markForCheck();
  }

  onEditModalCancel(): void {
    this.selectedEditInvoice = null;
    this.cdr.markForCheck();
  }

  onToggleExportPanel(event: Event): void {
    this.exportSuccessSubject.next(null);
    this.exportErrorSubject.next(null);
    this.csvExportPanel.toggle(event);
  }

  onConfirmExport(): void {
    this.exportContext$.pipe(take(1)).subscribe((context) => {
    const request: InvoiceExportRequest = {
      period: context.period,
      filters: context.filters
    };

    this.exportingSubject.next(true);
    this.exportSuccessSubject.next(null);
    this.exportErrorSubject.next(null);

    this.facade.exportCsv(request).pipe(
      switchMap((blob) => from(this.ensureCsvBom(blob))),
      finalize(() => {
        this.exportingSubject.next(false);
        this.cdr.markForCheck();
      })
    ).subscribe({
      next: (blob) => {
        DownloadUtil.download(blob, this.buildCsvFilename(context.period), 'text/csv');
        this.exportSuccessSubject.next('Download iniciado');
        this.cdr.markForCheck();
        timer(650).pipe(take(1)).subscribe(() => this.csvExportPanel.hide());
      },
      error: () => {
        this.exportErrorSubject.next('Não foi possível baixar o CSV. Tente novamente.');
        this.cdr.markForCheck();
      }
    });
    });
  }

  private buildChips(models: readonly IFilterModel[]): SelectedFilterChip[] {
    return models
      .map((m) => this.toChip(m))
      .filter((c): c is SelectedFilterChip => c !== null);
  }

  private buildExportContext(filters: readonly IFilterModel[]): ExportContext {
    const period = this.getPeriodFromFilters(filters);
    return {
      period,
      filters: this.getExportFilters(filters, period),
      hasActiveFilters: this.hasActiveExportFilters(filters, period)
    };
  }

  private getPeriodFromFilters(filters: readonly IFilterModel[]): InvoiceExportPeriod {
    const dateFilter = filters.find((model) => model.key === 'date');
    const start = this.getFilterDateValue(dateFilter, 'after');
    return this.createMonthPeriod(start ? this.parseLocalDate(start) : this.currentMonthDate);
  }

  private getExportFilters(filters: readonly IFilterModel[], period: InvoiceExportPeriod): readonly IFilter[] {
    const professionalFilter = filters.find((model) => model.key === 'professional');
    const statusFilter = filters.find((model) => model.key === 'status');
    const dateFilter = filters.find((model) => model.key === 'date');
    const startDate = this.getFilterDateValue(dateFilter, 'after');
    const endDate = this.getFilterDateValue(dateFilter, 'before');
    const periodFilters = this.createPeriodFilters({
      ...period,
      startDate: startDate ?? period.startDate,
      endDate: endDate ?? period.endDate
    });

    return [
      ...periodFilters,
      ...(professionalFilter?.filter ?? []),
      ...(statusFilter?.filter.length ? statusFilter.filter : this.createDefaultStatusFilters())
    ];
  }

  private hasActiveExportFilters(filters: readonly IFilterModel[], period: InvoiceExportPeriod): boolean {
    const dateFilter = filters.find((model) => model.key === 'date');
    const startDate = this.getFilterDateValue(dateFilter, 'after');
    const endDate = this.getFilterDateValue(dateFilter, 'before');

    return Boolean(
      filters.some((model) => model.key === 'professional' && model.filter.length > 0) ||
      filters.some((model) => model.key === 'status' && model.filter.length > 0) ||
      (startDate && startDate !== period.startDate) ||
      (endDate && endDate !== period.endDate)
    );
  }

  private createPeriodFilters(period: InvoiceExportPeriod): readonly IFilter[] {
    return [
      { key: 'date', fields: ['after'], property: 'revenueDate', value: period.startDate },
      { key: 'date', fields: ['before'], property: 'revenueDate', value: period.endDate }
    ];
  }

  private createDefaultStatusFilters(): readonly IFilter[] {
    return this.defaultExportStatuses.map((status) => ({
      key: 'status',
      property: 'status[]',
      value: status
    }));
  }

  private getFilterDateValue(model: IFilterModel | undefined, operator: 'after' | 'before'): string | null {
    const value = model?.filter.find((item) => item.fields?.includes(operator))?.value;
    return typeof value === 'string' ? value : null;
  }

  private buildExportButtonLabel(context: ExportContext, filteredTotal: number): string {
    const base = `Exportar CSV - ${this.getMonthName(context.period.month)}/${context.period.year}`;
    return context.hasActiveFilters ? `${base} - ${filteredTotal} filtradas` : base;
  }

  private createMonthPeriod(date: Date): InvoiceExportPeriod {
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const start = new Date(year, month - 1, 1);
    const end = new Date(year, month, 0);

    return {
      month,
      year,
      startDate: this.toLocalDateString(start),
      endDate: this.toLocalDateString(end)
    };
  }

  private parseLocalDate(value: string): Date {
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  private getMonthName(month: number): string {
    const names = [
      'Janeiro',
      'Fevereiro',
      'Março',
      'Abril',
      'Maio',
      'Junho',
      'Julho',
      'Agosto',
      'Setembro',
      'Outubro',
      'Novembro',
      'Dezembro'
    ];
    return names[month - 1] ?? '';
  }

  private buildCsvFilename(period: InvoiceExportPeriod): string {
    const month = this.getMonthName(period.month)
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
    return `notas_${month}_${period.year}.csv`;
  }

  private async ensureCsvBom(blob: Blob): Promise<Blob> {
    const csvContent = await blob.text();
    if (csvContent.startsWith('\uFEFF')) {
      return blob;
    }

    return new Blob(['\uFEFF', csvContent], {
      type: 'text/csv;charset=utf-8;'
    });
  }

  private toChip(model: IFilterModel): SelectedFilterChip | null {
    const label = this.filterLabelMap[model.key] ?? model.key;

    if (model.type === 'DATE' || model.type === 'MONTH') {
      const displayValue = this.getDateDisplayValue(model);
      if (!displayValue) return null;
      return { key: model.key, label, value: model, displayValue };
    }

    if (model.type === 'FILTER') {
      const checked = model.data.filter((d: any) => d.checked);
      if (checked.length === 0) return null;
      return {
        key: model.key,
        label,
        value: model,
        displayValue: checked.map((d: any) => d.name).join(', ')
      };
    }

    return null;
  }

  private getDateDisplayValue(model: IFilterModel): string | null {
    const afterFilter = model.filter.find((f) => f.fields?.includes('after'));
    const beforeFilter = model.filter.find((f) => f.fields?.includes('before'));

    if (afterFilter?.value && beforeFilter?.value) {
      return `${afterFilter.value} - ${beforeFilter.value}`;
    }

    const after = model.data.find((d: any) => d.id === 'after');
    const before = model.data.find((d: any) => d.id === 'before');
    if (!after?.name || !before?.name) return null;
    const start = after.name instanceof Date ? this.toLocalDateString(after.name) : after.name;
    const end = before.name instanceof Date ? this.toLocalDateString(before.name) : before.name;
    return `${start} - ${end}`;
  }

  private toLocalDateString(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
}
